import {
  IonContent,
  IonPage,
  useIonViewWillLeave,
  useIonViewWillEnter,
} from "@ionic/react";
import React from "react";
import { useHistory } from "react-router-dom";
import clsx from "clsx";

import { IOnClickStudentAvatarParams } from "../../../state";
import {
  checkRealizationKlass,
  clearSuccessMessage,
  clearRealizationKlasses,
  clearPagyInfo,
  Context,
  fetchRealizationKlasses,
  FETCH_REALIZATION_KLASSES_SUCCESS_MESSAGE,
  boxesPath,
  sharedRealizationListPath,
  CREATE_COMMENT_SUCCESS_MESSAGE,
  UPDATE_COMMENT_SUCCESS_MESSAGE,
  DELETE_COMMENT_SUCCESS_MESSAGE,
  fetchComments,
  createComment,
  updateComment,
  deleteComment,
  clearComments,
  clearFeedbacks,
  CREATE_REALIZATION_STAMP_REACTION_SUCCESS,
  createRealizationStampReaction,
  fetchRealizationConversations,
  clearConversations,
  addFavorite,
  removeFavorite,
} from "../../../store/teacher";
import { EMPTY_CLASS_MESSAGE } from "../../../constants/message";
import useWaitApiCall from "../../../hooks/useWaitApiCall";
import useDidMountEffect from "../../../hooks/useDidMountEffect";
import ThemeHeader from "../../../molecules/ThemeHeader";
import NavToolbar from "../../../organisms/teacher/NavToolbar";
import EmbeddedHeaderModal from "../../../organisms/EmbeddedHeaderModal";
import SharedRealization from "../../../templates/teacher/SharedRealization";
import SharedRealizationList from "../../../templates/teacher/SharedRealizationList";
import styles from "../common.module.scss";
import type { Stamp } from "../../../libs/stamp";

const SharedRealizationListPage = () => {
  const { dispatch, contextState } = React.useContext(Context);
  const [calling, peep] = useWaitApiCall(contextState);
  const history = useHistory();
  const [values, updateValues] = React.useState({
    selectedClassID: 0,
    selectedClassName: "すべてのグループ",
    isDisplaying: false,
    realizationDetailId: 0,
    entryId: 0,
  });
  const hasNoClasses =
    contextState.teacher.id !== 0 && contextState.teacher.classes.length === 0;

  const update = (args: { [key: string]: any }) => {
    updateValues(prevValues => ({ ...prevValues, ...args }));
  };

  const contentRef = React.useRef<HTMLIonContentElement>(null);

  const fetchRealizationKlassesFunc = React.useCallback(
    (class_id: number, page?: number) => {
      dispatch(peep(fetchRealizationKlasses(class_id, page ?? 1)));
    },
    [dispatch, peep],
  );

  const clearRealizationKlassesFunc = React.useCallback(() => {
    dispatch(clearRealizationKlasses());
  }, [dispatch]);

  React.useEffect(() => {
    if (
      contextState.success_message === FETCH_REALIZATION_KLASSES_SUCCESS_MESSAGE
    ) {
      dispatch(clearSuccessMessage());
      const entry = contextState.realization_klasses[0];
      if (entry) {
        if (contextState.unread_shared_per_class[`${values.selectedClassID}`]) {
          dispatch(checkRealizationKlass(values.selectedClassID));
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    dispatch,
    contextState.success_message,
    contextState.unread_shared_per_class,
    contextState.realization_klasses,
  ]);

  useIonViewWillEnter(() => {
    update({
      isDisplaying: true,
    });
    if (values.selectedClassID === 0) {
      fetchRealizationKlassesFunc(values.selectedClassID);
    } else {
      update({ selectedClassID: 0, selectedClassName: "すべてのグループ" });
    }
  }, [values.selectedClassID]);

  useDidMountEffect(
    () => fetchRealizationKlassesFunc(values.selectedClassID),
    [values.selectedClassID, fetchRealizationKlassesFunc],
  );

  useIonViewWillLeave(() => {
    update({
      isDisplaying: false,
    });
    dispatch(clearRealizationKlasses());
    dispatch(clearPagyInfo());
    clearRealizationFunc();
  });

  const realizationDetail = React.useMemo(() => {
    const entry = contextState.realization_klasses.find(
      entry => entry.id === values.entryId,
    );
    return (
      entry && {
        ...entry.realization,
        favorite_count: entry.favorite_count,
        is_favorite: entry.is_favorite,
      }
    );
  }, [contextState.realization_klasses, values.entryId]);

  React.useEffect(() => {
    if (
      contextState.success_message === CREATE_COMMENT_SUCCESS_MESSAGE ||
      contextState.success_message === UPDATE_COMMENT_SUCCESS_MESSAGE ||
      contextState.success_message === DELETE_COMMENT_SUCCESS_MESSAGE ||
      contextState.success_message === CREATE_REALIZATION_STAMP_REACTION_SUCCESS
    ) {
      if (values.realizationDetailId !== 0) {
        dispatch(clearSuccessMessage());
        dispatch(fetchComments(values.realizationDetailId));
        setTimeout(() => {
          contentRef.current?.scrollToBottom(500);
        }, 500);
      }
    }
  }, [dispatch, contextState.success_message, values.realizationDetailId]);

  React.useEffect(() => {
    if (values.realizationDetailId !== 0 && values.isDisplaying) {
      dispatch(fetchComments(values.realizationDetailId));
      dispatch(fetchRealizationConversations(values.realizationDetailId));
    }
  }, [dispatch, values.realizationDetailId, values.isDisplaying]);

  const createCommentFunc = React.useCallback(
    (realization_id: number, content: string) => {
      dispatch(peep(createComment(realization_id, content)));
    },
    [dispatch, peep],
  );

  const updateCommentFunc = React.useCallback(
    (comment_id: number, content: string) => {
      dispatch(peep(updateComment(comment_id, content)));
    },
    [dispatch, peep],
  );

  const deleteCommentFunc = React.useCallback(
    (comment_id: number) => {
      dispatch(peep(deleteComment(comment_id)));
    },
    [dispatch, peep],
  );

  const createStampReactionFunc = (realization_id: number, stamp: Stamp) => {
    dispatch(
      peep(
        createRealizationStampReaction(realization_id, {
          type: "comment",
          realization_stamp_id: stamp.id,
        }),
      ),
    );
  };

  const addFavoriteFunc = React.useCallback(
    (id: number) => {
      if (calling) return;
      dispatch(peep(addFavorite(id)));
    },
    [dispatch, peep, calling],
  );

  const removeFavoriteFunc = React.useCallback(
    (id: number) => {
      if (calling) return;
      dispatch(peep(removeFavorite(id)));
    },
    [dispatch, peep, calling],
  );

  const clearRealizationFunc = React.useCallback(() => {
    update({ realizationDetailId: 0, entryId: 0 });
    dispatch(clearComments());
    dispatch(clearFeedbacks());
    dispatch(clearConversations());
  }, [dispatch]);

  const showStudentDetailFunc = ({
    class_id,
    student_id,
  }: IOnClickStudentAvatarParams) => {
    window.location.href = `${process.env.REACT_APP_TEACHER_ONE_MENU_URL}/students/${class_id}/${student_id}`;
  };

  const teacherClassIds = React.useMemo(
    () => contextState.teacher.classes.map(c => c.id),
    [contextState.teacher.classes],
  );

  return (
    <IonPage>
      {realizationDetail && (
        <EmbeddedHeaderModal
          isOpen={!!values.realizationDetailId}
          title="詳細"
          onClose={clearRealizationFunc}
        >
          <IonContent ref={contentRef}>
            <SharedRealization
              calling={calling}
              realization={realizationDetail}
              comments={contextState.comments}
              conversations={contextState.conversations}
              current_id={contextState.teacher.id}
              createComment={createCommentFunc}
              updateComment={updateCommentFunc}
              deleteComment={deleteCommentFunc}
              createStampReaction={createStampReactionFunc}
              onAvatarClick={showStudentDetailFunc}
              teacherClassIds={teacherClassIds}
              addFavorite={addFavoriteFunc}
              removeFavorite={removeFavoriteFunc}
            />
          </IonContent>
        </EmbeddedHeaderModal>
      )}
      <IonContent className={`${styles.wrapper} ${styles.bgWhileBase}`}>
        <NavToolbar defaultSegment="timeline" />
        {contextState.teacher.classes.length > 0 && (
          <div className={clsx(styles.container, styles.hasToolbar)}>
            {contextState.geartheme && (
              <ThemeHeader geartheme={contextState.geartheme} isTeacher />
            )}
            <SharedRealizationList
              isDisplaying={values.isDisplaying}
              calling={calling}
              classes={contextState.teacher.classes}
              realization_klasses={contextState.realization_klasses}
              unread_shared_per_class={contextState.unread_shared_per_class}
              pagy_info={contextState.pagy_info}
              fetchRealizationKlasses={fetchRealizationKlassesFunc}
              clearRealizationKlasses={clearRealizationKlassesFunc}
              values={values}
              update={update}
              redirectToBox={box_id =>
                history.push(
                  `${sharedRealizationListPath}${boxesPath}/${box_id}`,
                )
              }
              onAction={(realizationDetailId, entryId) => {
                update({ realizationDetailId, entryId });
              }}
              showStudentDetail={showStudentDetailFunc}
              addFavorite={addFavoriteFunc}
              removeFavorite={removeFavoriteFunc}
            />
          </div>
        )}
        {hasNoClasses && (
          <div className={styles.container}>
            <p className={clsx(styles.positionMiddle, styles.message)}>
              {EMPTY_CLASS_MESSAGE}
            </p>
          </div>
        )}
      </IonContent>
    </IonPage>
  );
};

export default SharedRealizationListPage;
